import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { MandateStatus } from 'app/shared/model/enumerations/mandate-status.model';
import { createEntity, getEntity, reset, updateEntity } from './mandate.reducer';

export const MandateUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const mandateEntity = useAppSelector(state => state.mandate.entity);
  const loading = useAppSelector(state => state.mandate.loading);
  const updating = useAppSelector(state => state.mandate.updating);
  const updateSuccess = useAppSelector(state => state.mandate.updateSuccess);
  const mandateStatusValues = Object.keys(MandateStatus);

  const handleClose = () => {
    navigate('/mandate');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.bankAccountId !== undefined && typeof values.bankAccountId !== 'number') {
      values.bankAccountId = Number(values.bankAccountId);
    }
    if (values.signatureId !== undefined && typeof values.signatureId !== 'number') {
      values.signatureId = Number(values.signatureId);
    }
    values.signedAt = convertDateTimeToServer(values.signedAt);
    values.createdAt = convertDateTimeToServer(values.createdAt);
    values.updatedAt = convertDateTimeToServer(values.updatedAt);

    const entity = {
      ...mandateEntity,
      ...values,
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          signedAt: displayDefaultDateTime(),
          createdAt: displayDefaultDateTime(),
          updatedAt: displayDefaultDateTime(),
        }
      : {
          status: 'NEW',
          ...mandateEntity,
          signedAt: convertDateTimeFromServer(mandateEntity.signedAt),
          createdAt: convertDateTimeFromServer(mandateEntity.createdAt),
          updatedAt: convertDateTimeFromServer(mandateEntity.updatedAt),
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.mandate.home.createOrEditLabel" data-cy="MandateCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.mandate.home.createOrEditLabel">
              Create or edit a Mandate
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="mandate-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.bankAccountId')}
                id="mandate-bankAccountId"
                name="bankAccountId"
                data-cy="bankAccountId"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.mandateReference')}
                id="mandate-mandateReference"
                name="mandateReference"
                data-cy="mandateReference"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.status')}
                id="mandate-status"
                name="status"
                data-cy="status"
                type="select"
              >
                {mandateStatusValues.map(mandateStatus => (
                  <option value={mandateStatus} key={mandateStatus}>
                    {translate(`crawlerBackendApp.MandateStatus.${mandateStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.signatureId')}
                id="mandate-signatureId"
                name="signatureId"
                data-cy="signatureId"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.signedAt')}
                id="mandate-signedAt"
                name="signedAt"
                data-cy="signedAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.entranceCode')}
                id="mandate-entranceCode"
                name="entranceCode"
                data-cy="entranceCode"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  maxLength: { value: 40, message: translate('entity.validation.maxlength', { max: 40 }) },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.createdAt')}
                id="mandate-createdAt"
                name="createdAt"
                data-cy="createdAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.updatedAt')}
                id="mandate-updatedAt"
                name="updatedAt"
                data-cy="updatedAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.transactionUrl')}
                id="mandate-transactionUrl"
                name="transactionUrl"
                data-cy="transactionUrl"
                type="text"
                validate={{
                  maxLength: { value: 1024, message: translate('entity.validation.maxlength', { max: 1024 }) },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.mandateTransactionCreateRequestKey')}
                id="mandate-mandateTransactionCreateRequestKey"
                name="mandateTransactionCreateRequestKey"
                data-cy="mandateTransactionCreateRequestKey"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.mandateTransactionResponseKey')}
                id="mandate-mandateTransactionResponseKey"
                name="mandateTransactionResponseKey"
                data-cy="mandateTransactionResponseKey"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.mandate.mandateStatusResponseKey')}
                id="mandate-mandateStatusResponseKey"
                name="mandateStatusResponseKey"
                data-cy="mandateStatusResponseKey"
                type="text"
              />
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/mandate" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default MandateUpdate;
